// src/redux/slices/checklistSlice.js

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { config } from '../../config';

// Reconcile server checklists into local state without clobbering the array.
// A full replace (`state.checklists = payload`) changes every React key and
// drops any optimistic items that are still in flight from a *different*
// request — that is what made fast item-adds blink and occasionally vanish.
// Instead we merge by _id and only add server items we don't already have,
// dropping the local `pending`/`tempId` flags once the server confirms.
const reconcileChecklists = (state, serverChecklists) => {
    if (!Array.isArray(serverChecklists)) return;
    serverChecklists.forEach(serverCl => {
        const localCl = state.checklists.find(c => c._id === serverCl._id);
        if (!localCl) {
            state.checklists.push(serverCl);
            return;
        }
        // Merge scalar checklist fields (e.g. title) without replacing the object.
        if (serverCl.title !== undefined) localCl.title = serverCl.title;

        const serverItems = serverCl.items || [];
        const serverIds = new Set(serverItems.map(it => it._id));

        // Reconcile each server item into the local list, matching by _id.
        // We don't have a temp→real id mapping, so confirm pending placeholders
        // by title: the first unconfirmed pending item with the same title that
        // the server now reports becomes that real item (its key updates once).
        const toggling = state.togglingItemIds || [];
        const matchedPending = new Set();
        serverItems.forEach(serverItem => {
            const existing = localCl.items.find(it => it._id === serverItem._id);
            if (existing) {
                // If this item's check is mid-flight, keep our optimistic
                // `checked` so a stale echo doesn't flip it back-and-forth.
                const localChecked = existing.checked;
                Object.assign(existing, serverItem);
                if (toggling.includes(existing._id)) {
                    existing.checked = localChecked;
                }
                return;
            }
            const pendingMatch = localCl.items.find(
                it => it.pending && it.title === serverItem.title && !matchedPending.has(it._id)
            );
            if (pendingMatch) {
                matchedPending.add(pendingMatch._id);
                Object.assign(pendingMatch, serverItem, { pending: false, tempId: undefined });
            } else {
                localCl.items.push(serverItem);
            }
        });

        // Drop local items the server no longer has — but keep still-in-flight
        // optimistic items (other concurrent adds not yet in this response).
        localCl.items = localCl.items.filter(it => serverIds.has(it._id) || it.pending);
    });
};

// Async actions

export const fetchChecklists = createAsyncThunk(
    'checklists/fetchChecklists',
    async (cardId, { rejectWithValue }) => {
        try {
            const response = await fetch(config.API_URI + `/v1/cards/${cardId}/checklists`, {
                credentials: 'include',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                }
            });
            const data = await response.json();
            return data.checklists;
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const addChecklistItem = createAsyncThunk(
    'checklists/addChecklistItem',
    async ({ cardId, checklistId, newItem }, { rejectWithValue }) => {
        try {
            
            const response = await fetch(config.API_URI + `/v1/cards/${cardId}/checklists/${checklistId}/items`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                    
                },
                body: JSON.stringify({ title: newItem, checked: false })
            });
            const data = await response.json();
            return data.checklists;
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const updateChecklistItem = createAsyncThunk(
    'checklists/updateChecklistItem',
    async ({ cardId, checklistId, itemId, title, checked }, { rejectWithValue }) => {
        try {
            
            const response = await fetch(config.API_URI + `/v1/cards/${cardId}/checklists/${checklistId}/items/${itemId}`, {
                method: 'PUT',
                credentials: 'include',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                    
                },
                body: JSON.stringify({ title, checked })
            });
            const data = await response.json();
            return data.checklists;
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const deleteChecklistItem = createAsyncThunk(
    'checklists/deleteChecklistItem',
    async ({ cardId, checklistId, itemId }, { rejectWithValue }) => {
        try {
            
            await fetch(config.API_URI + `/v1/cards/${cardId}/checklists/${checklistId}/items/${itemId}`, {
                method: 'DELETE',
                credentials: 'include',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                    
                }
            });
            return { checklistId, itemId };
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const deleteChecklist = createAsyncThunk(
    'checklists/deleteChecklist',
    async ({ cardId, checklistId }, { rejectWithValue }) => {
        try {
            
            await fetch(config.API_URI + `/v1/cards/${cardId}/checklists/${checklistId}`, {
                method: 'DELETE',
                credentials: 'include',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                }
            });
            return { checklistId };
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

const checklistSlice = createSlice({
    name: 'checklists',
    initialState: {
        checklists: [],
        newChecklist: null,
        visibleAddNew: null,
        // IDs of items whose checked-state has an in-flight request. While an id
        // is here, reconcile keeps the local (optimistic) checked value so a
        // stale socket echo / slow response can't flip the box back and forth.
        togglingItemIds: [],
        status: 'idle',
        error: null
    },
    reducers: {
        localDeleteChecklist: (state, action) => {
            const { checklistId } = action.payload;
            state.checklists = state.checklists.filter(checklist => checklist._id !== checklistId);
        },
        // Optimistic local update for add checklist item
        localAddItem: (state, action) => {
            const { checklistId, newItem, tempId } = action.payload;
            const checklist = state.checklists.find(checklist => checklist._id === checklistId);
            if (checklist) {
                checklist.items.push({
                    _id: tempId, // Stable temp ID so React keys don't churn before reconciliation
                    tempId,
                    title: newItem,
                    checked: false,
                    pending: true
                });
            }
        },
        // Optimistic local update for update checklist item
        localUpdateItem: (state, action) => {
            const { checklistId, itemId, title, checked } = action.payload;
            const checklist = state.checklists.find(checklist => checklist._id === checklistId);
            if (checklist) {
                const item = checklist.items.find(item => item._id === itemId);
                if (item) {
                    item.title = title;
                    item.checked = checked;
                }
            }
        },
        // Mark an item's checked-state request as in-flight (lock reconcile).
        markItemToggling: (state, action) => {
            const itemId = action.payload;
            if (!state.togglingItemIds.includes(itemId)) {
                state.togglingItemIds.push(itemId);
            }
        },
        // Clear the lock once the request settles (success or revert).
        clearItemToggling: (state, action) => {
            const itemId = action.payload;
            state.togglingItemIds = state.togglingItemIds.filter(id => id !== itemId);
        },
        // Optimistic local update for delete checklist item
        localDeleteItem: (state, action) => {
            const { checklistId, itemId } = action.payload;
            const checklist = state.checklists.find(checklist => checklist._id === checklistId);
            if (checklist) {
                checklist.items = checklist.items.filter(item => item._id !== itemId);
            }
        },
        // Optimistic checklist addition
        localAddChecklist: (state, action) => {
            // Guard against duplicates: the CHECKLIST_ADDED socket event can
            // replace the array (via setChecklistsFromSocket) with the new
            // checklist already present before this HTTP-response push runs,
            // which previously rendered the same checklist twice.
            const exists = state.checklists.some(c => c._id === action.payload._id);
            if (!exists) {
                state.checklists.push(action.payload);
            }
            state.newChecklist = action.payload;
            state.visibleAddNew = action.payload._id
        },
        localNewChecklist: (state, action) => {
            state.newChecklist = action.payload;
        },
        setVisibleAddNew: (state, action) => {
            state.visibleAddNew = action.payload; // Update Redux state
        },
        // Optionally handle the error case where the API fails, you can revert the optimistic change here
        revertChecklistAddition: (state, action) => {
            const { tempId } = action.payload;
            state.checklists = state.checklists.filter(checklist => checklist.tempId !== tempId);
        },
        localReorderItems: (state, action) => {
            const { checklistId, sourceIndex, destinationIndex } = action.payload;
            const checklist = state.checklists.find(list => list._id === checklistId);
            if (checklist) {
                const items = [...checklist.items];
                const [movedItem] = items.splice(sourceIndex, 1);
                items.splice(destinationIndex, 0, movedItem);
                checklist.items = items;
            }
        },

        // ========== REAL-TIME OPTIMISTIC ACTIONS ==========

        // Add checklist from real-time event
        optimisticAddChecklist: (state, action) => {
            const checklist = action.payload;
            // Check if checklist already exists (avoid duplicates)
            const exists = state.checklists.some(c => c._id === checklist._id);
            if (!exists) {
                state.checklists.push(checklist);
            }
        },

        // Update checklist from real-time event
        optimisticUpdateChecklist: (state, action) => {
            const { checklistId, updates } = action.payload;
            const checklist = state.checklists.find(c => c._id === checklistId);
            if (checklist) {
                Object.assign(checklist, updates);
            }
        },

        // Delete checklist from real-time event
        optimisticDeleteChecklist: (state, action) => {
            const checklistId = action.payload;
            state.checklists = state.checklists.filter(c => c._id !== checklistId);
        },

        // Add checklist item from real-time event
        optimisticAddChecklistItem: (state, action) => {
            const { checklistId, item } = action.payload;
            const checklist = state.checklists.find(c => c._id === checklistId);
            if (checklist) {
                // Check if item already exists
                const exists = checklist.items.some(i => i._id === item._id);
                if (!exists) {
                    checklist.items.push(item);
                }
            }
        },

        // Update checklist item from real-time event
        optimisticUpdateChecklistItem: (state, action) => {
            const { checklistId, itemId, updates } = action.payload;
            const checklist = state.checklists.find(c => c._id === checklistId);
            if (checklist) {
                const item = checklist.items.find(i => i._id === itemId);
                if (item) {
                    Object.assign(item, updates);
                }
            }
        },

        // Delete checklist item from real-time event
        optimisticDeleteChecklistItem: (state, action) => {
            const { checklistId, itemId } = action.payload;
            const checklist = state.checklists.find(c => c._id === checklistId);
            if (checklist) {
                checklist.items = checklist.items.filter(i => i._id !== itemId);
            }
        },

        // Apply checklists from a real-time event. Reconcile (merge) rather than
        // blindly replace so a socket echo of the current user's own action does
        // not wipe still-in-flight optimistic items (which caused fast adds to
        // blink/disappear). Removes checklists the server no longer has.
        setChecklistsFromSocket: (state, action) => {
            const serverChecklists = action.payload;
            if (!Array.isArray(serverChecklists)) return;
            reconcileChecklists(state, serverChecklists);
            const serverIds = new Set(serverChecklists.map(c => c._id));
            state.checklists = state.checklists.filter(c => serverIds.has(c._id));
        },

    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchChecklists.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(fetchChecklists.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.checklists = action.payload;
            })
            .addCase(fetchChecklists.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload;
            })
            .addCase(addChecklistItem.fulfilled, (state, action) => {
                // Merge server data in place instead of replacing the whole
                // array, so concurrent optimistic adds aren't dropped (no blink).
                reconcileChecklists(state, action.payload);
            })
            .addCase(updateChecklistItem.fulfilled, (state, action) => {
                reconcileChecklists(state, action.payload);
            })
            .addCase(deleteChecklistItem.fulfilled, (state, action) => {
                const { checklistId, itemId } = action.payload;
                const checklist = state.checklists.find(checklist => checklist._id === checklistId);
                if (checklist) {
                    checklist.items = checklist.items.filter(item => item._id !== itemId);
                }
            })
            .addCase(deleteChecklist.fulfilled, (state, action) => {
                const { checklistId } = action.payload;
                state.checklists = state.checklists.filter(checklist => checklist._id !== checklistId);
            });

    }
});

// Export the reducers for optimistic UI
export const {
    localAddItem,
    localUpdateItem,
    localDeleteItem,
    localDeleteChecklist,
    localAddChecklist,
    localNewChecklist,
    setVisibleAddNew,
    revertChecklistAddition,
    localReorderItems,
    markItemToggling,
    clearItemToggling,
    // Real-time optimistic actions
    optimisticAddChecklist,
    optimisticUpdateChecklist,
    optimisticDeleteChecklist,
    optimisticAddChecklistItem,
    optimisticUpdateChecklistItem,
    optimisticDeleteChecklistItem,
    setChecklistsFromSocket,
} = checklistSlice.actions;
export default checklistSlice.reducer;
